import React, { useState, useEffect, useRef} from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const AUTH = {
  headers: {
    Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
  },
};

const OFFENSIVE_POSITIONS = ["QB", "RB", "WR", "TE", "FB", "UNK"];
const DEFENSIVE_POSITIONS = ["CB", "DB", "DE", "DL", "DT", "LB", "S"];
const KICKER_POSITIONS = ["PK", "K"];
const PUNTER_POSITIONS = ["P"];

function PlayerPage() {
  const { id } = useParams();
  const [player, setPlayer] = useState(null);
  const [weeklyStats, setWeeklyStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("weekly");

  // Chart toggles
  const [showPassYds, setShowPassYds] = useState(false);
  const [showRushYds, setShowRushYds] = useState(false);
  const [showRecYds, setShowRecYds] = useState(false);
  const [showTotalYds, setShowTotalYds] = useState(false);
  const [showPassAtt, setShowPassAtt] = useState(false);
  const [showPassComp, setShowPassComp] = useState(false);
  const [showPassTD, setShowPassTD] = useState(false);
  const [showPassInt, setShowPassInt] = useState(false);
  const [showRushAtt, setShowRushAtt] = useState(false);
  const [showRushTD, setShowRushTD] = useState(false);
  const [showReceptions, setShowReceptions] = useState(false);
  const [showRecTD, setShowRecTD] = useState(false);
  const [showTargets, setShowTargets] = useState(false);
  const [showDefTackles, setShowDefTackles] = useState(false);
  const [showDefSacks, setShowDefSacks] = useState(false);
  const [showDefInts, setShowDefInts] = useState(false);
  const [showDefPD, setShowDefPD] = useState(false);
  const [showKickFGM, setShowKickFGM] = useState(false);
  const [showKickFGA, setShowKickFGA] = useState(false);
  const [showPuntYds, setShowPuntYds] = useState(false);
  const [showPunts, setShowPunts] = useState(false);
  const [seasonStats, setSeasonStats] = useState(null);

  // Navbar dropdowns
  const [openDropdown, setOpenDropdown] = useState(null); // "teams" | "leaders" | "players" | null
  const teamsRef = useRef(null);
  const leadersRef = useRef(null);
  const playersRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenSection, setMobileOpenSection] = useState(null); // "teams" | "leaders" | "players" | null
  
  // Navbar search
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [navbarSearchTerm, setNavbarSearchTerm] = useState("");
  const [navbarResults, setNavbarResults] = useState([]);
  const [navbarSelectedIndex, setNavbarSelectedIndex] = useState(-1);
  const navbarInputRef = useRef(null);
  const navbarContainerRef = useRef(null);

// Close search + any dropdown when clicking outside
useEffect(() => {
  const handleClickOutside = (e) => {
    // Close expanded search
    if (
      navbarContainerRef.current &&
      !navbarContainerRef.current.contains(e.target)
    ) {
      setSearchExpanded(false);
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
    }

    // Close dropdowns
    if (
      teamsRef.current && !teamsRef.current.contains(e.target) &&
      leadersRef.current && !leadersRef.current.contains(e.target) &&
      playersRef.current && !playersRef.current.contains(e.target)
    ) {
      setOpenDropdown(null);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

  // ========== Navbar Search ==========
  useEffect(() => {
    if (!navbarSearchTerm.trim()) {
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
      return;
    }

    const search = async () => {
      try {
        const res = await axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-players?search=${encodeURIComponent(
            navbarSearchTerm
          )}`,
          AUTH
        );
        setNavbarResults(res.data.slice(0, 12));
        setNavbarSelectedIndex(-1);
      } catch (err) {
        console.error("Navbar search error:", err);
      }
    };

    const timeout = setTimeout(search, 400);
    return () => clearTimeout(timeout);
  }, [navbarSearchTerm]);

  // Close navbar results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        navbarContainerRef.current &&
        !navbarContainerRef.current.contains(e.target)
      ) {
        setNavbarResults([]);
        setNavbarSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

// Keyboard navigation – Navbar
useEffect(() => {
  const handleKeyDown = (e) => {
    if (!navbarResults.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setNavbarSelectedIndex((prev) =>
        Math.min(prev + 1, navbarResults.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setNavbarSelectedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter" && navbarSelectedIndex >= 0) {
      const selected = navbarResults[navbarSelectedIndex];
      const path =
        selected.type === "team"
          ? `/team/${selected.abbreviation}`
          : `/player/${selected.bdl_id}`;
      window.location.href = path;
      setNavbarSearchTerm("");
      setNavbarResults([]);
    } else if (e.key === "Escape") {
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
    }
  };

  document.addEventListener("keydown", handleKeyDown);
  return () => document.removeEventListener("keydown", handleKeyDown);
}, [navbarResults, navbarSelectedIndex]);

useEffect(() => {
  const fetchData = async () => {
    try {
      const [profileRes, weeklyRes, seasonRes] = await Promise.all([
        axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-player/${id}`,
          AUTH
        ),
        axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-season-player-stats/${id}?season=2026`,
          AUTH
        ),
        axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-player-season-totals/${id}?season=2026`,
          AUTH
        ),
      ]);

      setPlayer(profileRes.data.player || profileRes.data);

      const sorted = (weeklyRes.data || []).sort((a, b) => a.week - b.week);
      setWeeklyStats(sorted);
      setSeasonStats(seasonRes.data || {});

      // Auto-check main yards
      const totals = sorted.reduce(
        (acc, s) => ({
          pass_yards: acc.pass_yards + (s.pass_yards || 0),
          rush_yards: acc.rush_yards + (s.rush_yards || 0),
          rec_yards: acc.rec_yards + (s.rec_yards || 0),
        }),
        { pass_yards: 0, rush_yards: 0, rec_yards: 0 }
      );

      setShowPassYds(totals.pass_yards > 0);
      setShowRushYds(totals.rush_yards > 0);
      setShowRecYds(totals.rec_yards > 0);
      setShowTotalYds(
        totals.pass_yards + totals.rush_yards + totals.rec_yards > 0
      );
    } catch (err) {
      console.error("PlayerPage error:", err);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-3xl text-white">
        Loading Player...
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-3xl text-red-500">
        Player not found
      </div>
    );
  }

  const position = player.position || "UNK";
  const isOffense = OFFENSIVE_POSITIONS.includes(position);
  const isDefense = DEFENSIVE_POSITIONS.includes(position);
  const isKicker = KICKER_POSITIONS.includes(position);
  const isPunter = PUNTER_POSITIONS.includes(position);

  const chartData = weeklyStats.map((s) => ({
    week: `Week ${s.week}`,
    "Pass Yds": s.pass_yards || 0,
    "Rush Yds": s.rush_yards || 0,
    "Rec Yds": s.rec_yards || 0,
    "Total Yds": (s.pass_yards || 0) + (s.rush_yards || 0) + (s.rec_yards || 0),
    "Pass Att": s.pass_attempts || 0,
    "Pass Comp": s.pass_completions || 0,
    "Pass TD": s.pass_tds || 0,
    "Pass Int": s.pass_ints || 0,
    "Rush Att": s.rush_attempts || 0,
    "Rush TD": s.rush_tds || 0,
    Receptions: s.receptions || 0,
    "Rec TD": s.rec_tds || 0,
    Targets: s.rec_targets || 0,
    Tackles: s.solo_tackles || 0,
    Sacks: s.def_sacks || 0,
    "Def Int": s.def_ints || 0,
    "Pass Def": s.def_pass_defended || 0,
    "FG Made": s.kick_fgm || 0,
    "FG Att": s.kick_fga || 0,
    "Punt Yds": s.punt_yards || 0,
    Punts: s.punt_punts || 0,
  }));

  // Season totals (summed from weekly)
  const seasonTotals = {
    pass_yards: weeklyStats.reduce((sum, s) => sum + (s.pass_yards || 0), 0),
    pass_tds: weeklyStats.reduce((sum, s) => sum + (s.pass_tds || 0), 0),
    pass_ints: weeklyStats.reduce((sum, s) => sum + (s.pass_ints || 0), 0),
    rush_yards: weeklyStats.reduce((sum, s) => sum + (s.rush_yards || 0), 0),
    rush_tds: weeklyStats.reduce((sum, s) => sum + (s.rush_tds || 0), 0),
    receptions: weeklyStats.reduce((sum, s) => sum + (s.receptions || 0), 0),
    rec_yards: weeklyStats.reduce((sum, s) => sum + (s.rec_yards || 0), 0),
    rec_tds: weeklyStats.reduce((sum, s) => sum + (s.rec_tds || 0), 0),
    targets: weeklyStats.reduce((sum, s) => sum + (s.rec_targets || 0), 0),
    tackles: weeklyStats.reduce((sum, s) => sum + (s.solo_tackles || 0), 0),
    sacks: weeklyStats.reduce((sum, s) => sum + (s.def_sacks || 0), 0),
    def_ints: weeklyStats.reduce((sum, s) => sum + (s.def_ints || 0), 0),
    pass_def: weeklyStats.reduce((sum, s) => sum + (s.def_pass_defended || 0), 0),
    fg_made: weeklyStats.reduce((sum, s) => sum + (s.kick_fgm || 0), 0),
    fg_att: weeklyStats.reduce((sum, s) => sum + (s.kick_fga || 0), 0),
    punts: weeklyStats.reduce((sum, s) => sum + (s.punt_punts || 0), 0),
    punt_yards: weeklyStats.reduce((sum, s) => sum + (s.punt_yards || 0), 0),
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans">
      {/* NAVBAR */}
      <nav className="fixed top-0 w-full bg-black/90 backdrop-blur-md z-50 border-b border-red-500/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Left: Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/images/HOFLogoSquare.png"
              alt="HOF Logo"
              className="w-12 h-12 object-contain"
            />
            <div>
              <div className="text-2xl font-black tracking-tighter">
                HELMETS OFF
              </div>
              <div className="text-[10px] text-red-400 -mt-1">FOOTBALL</div>
            </div>
          </Link>

          <div className="flex-1 hidden md:block"></div>

{/* Right side */}
<div className="flex items-center gap-4">
  
  {/* Collapsible Search (works on all screen sizes) */}
  <div className="relative" ref={navbarContainerRef}>
    {!searchExpanded ? (
      <button
        onClick={() => {
          setSearchExpanded(true);
          setTimeout(() => navbarInputRef.current?.focus(), 50);
        }}
        className="p-2 text-zinc-400 hover:text-white transition"
        aria-label="Search"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </button>
    ) : (
      <div className="relative w-64 sm:w-72">
        <input
          ref={navbarInputRef}
          type="text"
          value={navbarSearchTerm}
          onChange={(e) => setNavbarSearchTerm(e.target.value)}
          placeholder="Search players..."
          className="w-full bg-zinc-900 border border-zinc-700 rounded-full py-3 px-5 pl-12 text-sm focus:outline-none focus:border-red-500"
          autoFocus
        />
        <span className="absolute left-4 top-3.5 text-zinc-400">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </span>

        {navbarResults.length > 0 && (
          <div className="absolute mt-2 w-full bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50 max-h-64 overflow-y-auto">
            {navbarResults.map((item, index) => (
              <Link
                key={`${item.type}-${item.bdl_id}`}
                to={item.type === "team" ? `/team/${item.abbreviation}` : `/player/${item.bdl_id}`}
                className={`block px-4 py-3 hover:bg-zinc-800 ${navbarSelectedIndex === index ? "bg-zinc-800" : ""}`}
                onClick={() => {
                  setNavbarSearchTerm("");
                  setNavbarResults([]);
                  setSearchExpanded(false);
                }}
              >
                {item.type === "team" ? (
                  <>
                    <span className="font-medium">{item.name}</span>
                    <span className="text-zinc-500 text-sm"> • Team</span>
                  </>
                ) : (
                  <>
                    {item.first_name} {item.last_name} • {item.position_abbreviation}
                  </>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    )}
  </div>

  {/* Desktop Nav Links */}
  <div className="hidden md:flex items-center gap-8 text-sm font-medium">
    {/* TEAMS */}
    <div className="relative" ref={teamsRef}>
      <button
        onClick={() => setOpenDropdown(openDropdown === "teams" ? null : "teams")}
        className="hover:text-red-400 transition"
      >
        TEAMS
      </button>
      {openDropdown === "teams" && (
        <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
          <Link to="/teams" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>All Teams</Link>
          <Link to="/Standings" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Standings</Link>
          <Link to="/Schedule" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Schedule</Link>
        </div>
      )}
    </div>

    {/* LEADERS */}
    <div className="relative" ref={leadersRef}>
      <button
        onClick={() => setOpenDropdown(openDropdown === "leaders" ? null : "leaders")}
        className="hover:text-red-400 transition"
      >
        LEADERS
      </button>
      {openDropdown === "leaders" && (
        <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
          <Link to="/leaderboards" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>Season Leaders</Link>
          <Link to="/leaderboards?stat=passing_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Passing</Link>
          <Link to="/leaderboards?stat=rushing_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Rushing</Link>
          <Link to="/leaderboards?stat=receiving_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Receiving</Link>
        </div>
      )}
    </div>

    {/* PLAYERS */}
    <div className="relative" ref={playersRef}>
      <button
        onClick={() => setOpenDropdown(openDropdown === "players" ? null : "players")}
        className="hover:text-red-400 transition"
      >
        PLAYERS
      </button>
      {openDropdown === "players" && (
        <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
          <Link to="/playerStats" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>Player Stats</Link>
          <Link to="/playerCompare" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Player compare</Link>
        </div>
      )}
    </div>
  </div>

  {/* Mobile Hamburger Button */}
  <button
    className="md:hidden p-2 text-zinc-400 hover:text-white"
    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
    aria-label="Menu"
  >
    {mobileMenuOpen ? (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    )}
  </button>
</div>

        </div>

{/* Mobile Menu - collapsible sections */}
{mobileMenuOpen && (
  <div className="md:hidden absolute top-full left-0 w-full bg-black/95 backdrop-blur-md border-b border-zinc-800 px-6 py-5 z-40">
    
    {/* TEAMS */}
    <div className="border-b border-zinc-800">
      <button
        onClick={() =>
          setMobileOpenSection(mobileOpenSection === "teams" ? null : "teams")
        }
        className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
      >
        TEAMS
        <span className="text-zinc-500 text-sm">
          {mobileOpenSection === "teams" ? "−" : "+"}
        </span>
      </button>

      {mobileOpenSection === "teams" && (
        <div className="pb-4 space-y-3 pl-3">
          <Link to="/teams" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            All Teams
          </Link>
          <Link to="/Standings" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Standings
          </Link>
          <Link to="/Schedule" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Schedule
          </Link>
        </div>
      )}
    </div>

    {/* LEADERS */}
    <div className="border-b border-zinc-800">
      <button
        onClick={() =>
          setMobileOpenSection(mobileOpenSection === "leaders" ? null : "leaders")
        }
        className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
      >
        LEADERS
        <span className="text-zinc-500 text-sm">
          {mobileOpenSection === "leaders" ? "−" : "+"}
        </span>
      </button>

      {mobileOpenSection === "leaders" && (
        <div className="pb-4 space-y-3 pl-3">
          <Link to="/leaderboards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Season Leaders
          </Link>
          <Link to="/leaderboards?stat=passing_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Passing
          </Link>
          <Link to="/leaderboards?stat=rushing_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Rushing
          </Link>
          <Link to="/leaderboards?stat=receiving_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Receiving
          </Link>
        </div>
      )}
    </div>

    {/* PLAYERS */}
    <div>
      <button
        onClick={() =>
          setMobileOpenSection(mobileOpenSection === "players" ? null : "players")
        }
        className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
      >
        PLAYERS
        <span className="text-zinc-500 text-sm">
          {mobileOpenSection === "players" ? "−" : "+"}
        </span>
      </button>

      {mobileOpenSection === "players" && (
        <div className="pb-4 space-y-3 pl-3">
          <Link to="/playerStats" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Player Stats
          </Link>
          <Link to="/playerCompare" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Player Comparison
          </Link>
         </div>
      )}
    </div>

  </div>
)}
      </nav>

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-6">
          {/* TOP SECTION */}
          <div className="bg-zinc-900 rounded-3xl shadow-2xl p-10 mb-12 text-center">
            <h1 className="text-6xl font-black mb-3">{player.name}</h1>
            <p className="text-3xl text-zinc-400 mb-10">
              {player.full_position || player.position} •{" "}
              {player.full_team_name || player.team_abbr}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-6xl mx-auto">
              {[
                { label: "Height", value: player.height },
                { label: "Weight", value: player.weight },
                {
                  label: "Jersey",
                  value: player.jersey_number
                    ? `#${player.jersey_number}`
                    : "—",
                },
                { label: "Age", value: player.age },
                { label: "College", value: player.college },
                { label: "Experience", value: player.experience || "Rookie" },
              ].map((item, i) => (
                <div key={i} className="bg-zinc-800 p-5 rounded-2xl">
                  <div className="text-sm text-zinc-500 uppercase tracking-wider">
                    {item.label}
                  </div>
                  <div className="text-xl font-bold mt-2">
                    {item.value || "—"}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SEASON TREND CHART */}
          <div className="bg-zinc-900 rounded-3xl shadow-2xl p-8 mb-12">
            <h2 className="text-4xl font-black text-center mb-10">
              2026 Season Trend
            </h2>

            <div className="h-96 mb-10">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="week" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#18181B",
                      border: "none",
                    }}
                  />
                  <Legend />
                  {showPassYds && (
                    <Line type="monotone" dataKey="Pass Yds" stroke="#3b82f6" strokeWidth={3} />
                  )}
                  {showRushYds && (
                    <Line type="monotone" dataKey="Rush Yds" stroke="#10b981" strokeWidth={3} />
                  )}
                  {showRecYds && (
                    <Line type="monotone" dataKey="Rec Yds" stroke="#f59e0b" strokeWidth={3} />
                  )}
                  {showTotalYds && (
                    <Line type="monotone" dataKey="Total Yds" stroke="#8b5cf6" strokeWidth={4} />
                  )}
                  {showPassAtt && (
                    <Line type="monotone" dataKey="Pass Att" stroke="#60a5fa" strokeWidth={2} />
                  )}
                  {showPassComp && (
                    <Line type="monotone" dataKey="Pass Comp" stroke="#93c5fd" strokeWidth={2} />
                  )}
                  {showPassTD && (
                    <Line type="monotone" dataKey="Pass TD" stroke="#2563eb" strokeWidth={2} />
                  )}
                  {showPassInt && (
                    <Line type="monotone" dataKey="Pass Int" stroke="#ef4444" strokeWidth={2} />
                  )}
                  {showRushAtt && (
                    <Line type="monotone" dataKey="Rush Att" stroke="#34d399" strokeWidth={2} />
                  )}
                  {showRushTD && (
                    <Line type="monotone" dataKey="Rush TD" stroke="#059669" strokeWidth={2} />
                  )}
                  {showReceptions && (
                    <Line type="monotone" dataKey="Receptions" stroke="#fbbf24" strokeWidth={2} />
                  )}
                  {showRecTD && (
                    <Line type="monotone" dataKey="Rec TD" stroke="#d97706" strokeWidth={2} />
                  )}
                  {showTargets && (
                    <Line type="monotone" dataKey="Targets" stroke="#fcd34d" strokeWidth={2} />
                  )}
                  {showDefTackles && (
                    <Line type="monotone" dataKey="Tackles" stroke="#a78bfa" strokeWidth={2} />
                  )}
                  {showDefSacks && (
                    <Line type="monotone" dataKey="Sacks" stroke="#8b5cf6" strokeWidth={2} />
                  )}
                  {showDefInts && (
                    <Line type="monotone" dataKey="Def Int" stroke="#7c3aed" strokeWidth={2} />
                  )}
                  {showDefPD && (
                    <Line type="monotone" dataKey="Pass Def" stroke="#c4b5fd" strokeWidth={2} />
                  )}
                  {showKickFGM && (
                    <Line type="monotone" dataKey="FG Made" stroke="#22c55e" strokeWidth={2} />
                  )}
                  {showKickFGA && (
                    <Line type="monotone" dataKey="FG Att" stroke="#16a34a" strokeWidth={2} />
                  )}
                  {showPuntYds && (
                    <Line type="monotone" dataKey="Punt Yds" stroke="#0ea5e9" strokeWidth={2} />
                  )}
                  {showPunts && (
                    <Line type="monotone" dataKey="Punts" stroke="#0284c7" strokeWidth={2} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Checkboxes Grid */}
            <div className="bg-zinc-800/50 rounded-2xl p-6">
              {isOffense && (
                <div>
                  <h4 className="text-sm font-bold text-blue-400 mb-4 tracking-wider">
                    OFFENSE
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {[
                      { label: "Pass Yards", state: showPassYds, set: setShowPassYds },
                      { label: "Rush Yards", state: showRushYds, set: setShowRushYds },
                      { label: "Rec Yards", state: showRecYds, set: setShowRecYds },
                      { label: "Total Yards", state: showTotalYds, set: setShowTotalYds },
                      { label: "Pass Attempts", state: showPassAtt, set: setShowPassAtt },
                      { label: "Pass Completions", state: showPassComp, set: setShowPassComp },
                      { label: "Pass TDs", state: showPassTD, set: setShowPassTD },
                      { label: "Pass INTs", state: showPassInt, set: setShowPassInt },
                      { label: "Rush Attempts", state: showRushAtt, set: setShowRushAtt },
                      { label: "Rush TDs", state: showRushTD, set: setShowRushTD },
                      { label: "Receptions", state: showReceptions, set: setShowReceptions },
                      { label: "Rec TDs", state: showRecTD, set: setShowRecTD },
                      { label: "Targets", state: showTargets, set: setShowTargets },
                    ].map((item) => (
                      <label
                        key={item.label}
                        className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-4 py-3 rounded-xl transition"
                      >
                        <input
                          type="checkbox"
                          checked={item.state}
                          onChange={() => item.set(!item.state)}
                          className="w-4 h-4 accent-red-500"
                        />
                        <span className="text-sm">{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {isDefense && (
                <div>
                  <h4 className="text-sm font-bold text-purple-400 mb-4 tracking-wider">
                    DEFENSE
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {[
                      { label: "Tackles", state: showDefTackles, set: setShowDefTackles },
                      { label: "Sacks", state: showDefSacks, set: setShowDefSacks },
                      { label: "Def INTs", state: showDefInts, set: setShowDefInts },
                      { label: "Pass Defended", state: showDefPD, set: setShowDefPD },
                    ].map((item) => (
                      <label
                        key={item.label}
                        className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-4 py-3 rounded-xl transition"
                      >
                        <input
                          type="checkbox"
                          checked={item.state}
                          onChange={() => item.set(!item.state)}
                          className="w-4 h-4 accent-red-500"
                        />
                        <span className="text-sm">{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {(isKicker || isPunter) && (
                <div>
                  <h4 className="text-sm font-bold text-green-400 mb-4 tracking-wider">
                    SPECIAL TEAMS
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {isKicker && (
                      <>
                        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-4 py-3 rounded-xl transition">
                          <input
                            type="checkbox"
                            checked={showKickFGM}
                            onChange={() => setShowKickFGM(!showKickFGM)}
                            className="w-4 h-4 accent-red-500"
                          />
                          <span className="text-sm">FG Made</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-4 py-3 rounded-xl transition">
                          <input
                            type="checkbox"
                            checked={showKickFGA}
                            onChange={() => setShowKickFGA(!showKickFGA)}
                            className="w-4 h-4 accent-red-500"
                          />
                          <span className="text-sm">FG Attempts</span>
                        </label>
                      </>
                    )}
                    {isPunter && (
                      <>
                        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-4 py-3 rounded-xl transition">
                          <input
                            type="checkbox"
                            checked={showPuntYds}
                            onChange={() => setShowPuntYds(!showPuntYds)}
                            className="w-4 h-4 accent-red-500"
                          />
                          <span className="text-sm">Punt Yards</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-4 py-3 rounded-xl transition">
                          <input
                            type="checkbox"
                            checked={showPunts}
                            onChange={() => setShowPunts(!showPunts)}
                            className="w-4 h-4 accent-red-500"
                          />
                          <span className="text-sm">Punts</span>
                        </label>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* TABS: Weekly + Season */}
          <div className="bg-zinc-900 rounded-3xl shadow-2xl overflow-hidden">
            <div className="flex border-b border-zinc-700">
              <button
                onClick={() => setActiveTab("weekly")}
                className={`flex-1 py-5 text-xl font-bold transition ${
                  activeTab === "weekly"
                    ? "bg-red-600 text-white"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                WEEKLY STATS
              </button>
              <button
                onClick={() => setActiveTab("season")}
                className={`flex-1 py-5 text-xl font-bold transition ${
                  activeTab === "season"
                    ? "bg-red-600 text-white"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                SEASON STATS
              </button>
            </div>

{/* WEEKLY TAB */}
{activeTab === "weekly" && (
  <div className="p-8">
    <div className="overflow-x-auto">
      <table className="w-full text-left min-w-[900px]">
        <thead>
          <tr className="border-b border-zinc-700">
            <th className="py-4 px-5">Week</th>

            {/* QB */}
            {position === "QB" && (
              <>
                <th className="py-4 px-5">Pass Yds</th>
                <th className="py-4 px-5">Pass TD</th>
                <th className="py-4 px-5">INT</th>
                <th className="py-4 px-5">Rush Att</th>
                <th className="py-4 px-5">Rush Yds</th>
                <th className="py-4 px-5">Rush TD</th>
                <th className="py-4 px-5">Rec</th>
                <th className="py-4 px-5">Rec Yds</th>
              </>
            )}

            {/* RB / FB */}
            {(position === "RB" || position === "FB") && (
              <>
                <th className="py-4 px-5">Rush Att</th>
                <th className="py-4 px-5">Rush Yds</th>
                <th className="py-4 px-5">Rush TD</th>
                <th className="py-4 px-5">Rec</th>
                <th className="py-4 px-5">Rec Yds</th>
                <th className="py-4 px-5">Rec TD</th>
              </>
            )}

            {/* WR / TE */}
            {(position === "WR" || position === "TE") && (
              <>
                <th className="py-4 px-5">Rec</th>
                <th className="py-4 px-5">Rec Yds</th>
                <th className="py-4 px-5">Rec TD</th>
                <th className="py-4 px-5">Targets</th>
                <th className="py-4 px-5">Rush Att</th>
                <th className="py-4 px-5">Rush Yds</th>
              </>
            )}

            {/* Defense */}
            {["CB", "DB", "DE", "DL", "DT", "LB", "S"].includes(position) && (
              <>
                <th className="py-4 px-5">Tackles</th>
                <th className="py-4 px-5">Sacks</th>
                <th className="py-4 px-5">INT</th>
                <th className="py-4 px-5">PD</th>
              </>
            )}

            {/* Kicker */}
            {(position === "PK" || position === "K") && (
              <>
                <th className="py-4 px-5">FG Made</th>
                <th className="py-4 px-5">FG Att</th>
              </>
            )}

            {/* Punter */}
            {position === "P" && (
              <>
                <th className="py-4 px-5">Punts</th>
                <th className="py-4 px-5">Punt Yds</th>
              </>
            )}
          </tr>
        </thead>

        <tbody>
          {weeklyStats.map((s) => (
            <tr key={s.week} className="border-b border-zinc-800 hover:bg-zinc-800/50">
              <td className="py-4 px-5 font-medium">Week {s.week}</td>

              {/* QB */}
              {position === "QB" && (
                <>
                  <td className="py-4 px-5">{s.pass_yards || 0}</td>
                  <td className="py-4 px-5">{s.pass_tds || 0}</td>
                  <td className="py-4 px-5">{s.pass_ints || 0}</td>
                  <td className="py-4 px-5">{s.rush_attempts || 0}</td>
                  <td className="py-4 px-5">{s.rush_yards || 0}</td>
                  <td className="py-4 px-5">{s.rush_tds || 0}</td>
                  <td className="py-4 px-5">{s.receptions || 0}</td>
                  <td className="py-4 px-5">{s.rec_yards || 0}</td>
                </>
              )}

              {/* RB / FB */}
              {(position === "RB" || position === "FB") && (
                <>
                  <td className="py-4 px-5">{s.rush_attempts || 0}</td>
                  <td className="py-4 px-5">{s.rush_yards || 0}</td>
                  <td className="py-4 px-5">{s.rush_tds || 0}</td>
                  <td className="py-4 px-5">{s.receptions || 0}</td>
                  <td className="py-4 px-5">{s.rec_yards || 0}</td>
                  <td className="py-4 px-5">{s.rec_tds || 0}</td>
                </>
              )}

              {/* WR / TE */}
              {(position === "WR" || position === "TE") && (
                <>
                  <td className="py-4 px-5">{s.receptions || 0}</td>
                  <td className="py-4 px-5">{s.rec_yards || 0}</td>
                  <td className="py-4 px-5">{s.rec_tds || 0}</td>
                  <td className="py-4 px-5">{s.rec_targets || 0}</td>
                  <td className="py-4 px-5">{s.rush_attempts || 0}</td>
                  <td className="py-4 px-5">{s.rush_yards || 0}</td>
                </>
              )}

              {/* Defense */}
              {["CB", "DB", "DE", "DL", "DT", "LB", "S"].includes(position) && (
                <>
                  <td className="py-4 px-5">{s.solo_tackles || 0}</td>
                  <td className="py-4 px-5">{s.def_sacks || 0}</td>
                  <td className="py-4 px-5">{s.def_ints || 0}</td>
                  <td className="py-4 px-5">{s.def_pass_defended || 0}</td>
                </>
              )}

              {/* Kicker */}
              {(position === "PK" || position === "K") && (
                <>
                  <td className="py-4 px-5">{s.kick_fgm || 0}</td>
                  <td className="py-4 px-5">{s.kick_fga || 0}</td>
                </>
              )}

              {/* Punter */}
              {position === "P" && (
                <>
                  <td className="py-4 px-5">{s.punt_punts || 0}</td>
                  <td className="py-4 px-5">{s.punt_yards || 0}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}

{/* SEASON TAB */}
{activeTab === "season" && (
  <div className="p-8 space-y-10">
    {/* PASSING */}
{/* PASSING SECTION */}
{(position === "QB" || position === "RB" || position === "WR" || position === "TE") && (
  <div>
    <h3 className="text-2xl font-bold text-blue-400 mb-6">Passing</h3>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
      
      {/* Always show these for anyone with passing stats */}
      <div className="bg-zinc-800 rounded-2xl p-6 text-center">
        <div className="text-sm text-zinc-500 mb-1">Pass Yards</div>
        <div className="text-3xl font-black">{seasonStats?.passing_yards ?? 0}</div>
      </div>
      <div className="bg-zinc-800 rounded-2xl p-6 text-center">
        <div className="text-sm text-zinc-500 mb-1">Pass TDs</div>
        <div className="text-3xl font-black">{seasonStats?.passing_touchdowns ?? 0}</div>
      </div>
      <div className="bg-zinc-800 rounded-2xl p-6 text-center">
        <div className="text-sm text-zinc-500 mb-1">INTs</div>
        <div className="text-3xl font-black">{seasonStats?.passing_interceptions ?? 0}</div>
      </div>
      <div className="bg-zinc-800 rounded-2xl p-6 text-center">
        <div className="text-sm text-zinc-500 mb-1">Comp %</div>
        <div className="text-3xl font-black">
          {seasonStats?.passing_completion_pct
            ? Number(seasonStats.passing_completion_pct).toFixed(1)
            : 0}%
        </div>
      </div>

      {/* Specialized stats – only for QBs */}
      {position === "QB" && (
        <div className="bg-zinc-800 rounded-2xl p-6 text-center">
          <div className="text-sm text-zinc-500 mb-1">QBR</div>
          <div className="text-3xl font-black">
            {seasonStats?.qbr ? Number(seasonStats.qbr).toFixed(1) : 0}
          </div>
        </div>
      )}

      {/* You can keep adding more specialized ones the same way */}
      {position === "QB" && (
        <div className="bg-zinc-800 rounded-2xl p-6 text-center">
          <div className="text-sm text-zinc-500 mb-1">Pass Attempts</div>
          <div className="text-3xl font-black">{seasonStats?.passing_attempts ?? 0}</div>
        </div>
      )}
    </div>
  </div>
)}

    {/* RUSHING */}
    {(position === "QB" || position === "RB" || position === "FB" || position === "WR" || position === "TE") && (
      <div>
        <h3 className="text-2xl font-bold text-green-400 mb-6">Rushing</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">Rush Attempts</div>
            <div className="text-3xl font-black">{seasonStats?.rushing_attempts ?? 0}</div>
          </div>
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">Rush Yards</div>
            <div className="text-3xl font-black">{seasonStats?.rushing_yards ?? 0}</div>
          </div>
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">Rush TDs</div>
            <div className="text-3xl font-black">{seasonStats?.rushing_touchdowns ?? 0}</div>
          </div>
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">Yards / Att</div>
            <div className="text-3xl font-black">
              {seasonStats?.yards_per_rush_attempt
                ? Number(seasonStats.yards_per_rush_attempt).toFixed(1)
                : 0}
            </div>
          </div>
        </div>
      </div>
    )}

    {/* RECEIVING */}
    {(position === "QB" || position === "RB" || position === "FB" || position === "WR" || position === "TE") && (
      <div>
        <h3 className="text-2xl font-bold text-amber-400 mb-6">Receiving</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">Receptions</div>
            <div className="text-3xl font-black">{seasonStats?.receptions ?? 0}</div>
          </div>
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">Rec Yards</div>
            <div className="text-3xl font-black">{seasonStats?.receiving_yards ?? 0}</div>
          </div>
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">Rec TDs</div>
            <div className="text-3xl font-black">{seasonStats?.receiving_touchdowns ?? 0}</div>
          </div>
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">Targets</div>
            <div className="text-3xl font-black">{seasonStats?.receiving_targets ?? 0}</div>
          </div>
        </div>
      </div>
    )}

    {/* DEFENSE */}
    {["CB", "DB", "DE", "DL", "DT", "LB", "S"].includes(position) && (
      <div>
        <h3 className="text-2xl font-bold text-purple-400 mb-6">Defense</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">Total Tackles</div>
            <div className="text-3xl font-black">{seasonStats?.total_tackles ?? 0}</div>
          </div>
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">Sacks</div>
            <div className="text-3xl font-black">{seasonStats?.defensive_sacks ?? 0}</div>
          </div>
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">Interceptions</div>
            <div className="text-3xl font-black">{seasonStats?.defensive_interceptions ?? 0}</div>
          </div>
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">Forced Fumbles</div>
            <div className="text-3xl font-black">{seasonStats?.fumbles_forced ?? 0}</div>
          </div>
        </div>
      </div>
    )}

    {/* KICKING */}
    {(position === "PK" || position === "K") && (
      <div>
        <h3 className="text-2xl font-bold text-green-400 mb-6">Kicking</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">FG Made</div>
            <div className="text-3xl font-black">{seasonStats?.field_goals_made ?? 0}</div>
          </div>
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">FG Attempts</div>
            <div className="text-3xl font-black">{seasonStats?.field_goal_attempts ?? 0}</div>
          </div>
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">FG %</div>
            <div className="text-3xl font-black">
              {seasonStats?.field_goal_pct
                ? Number(seasonStats.field_goal_pct).toFixed(1)
                : 0}%
            </div>
          </div>
        </div>
      </div>
    )}

    {/* PUNTING */}
    {position === "P" && (
      <div>
        <h3 className="text-2xl font-bold text-sky-400 mb-6">Punting</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">Punts</div>
            <div className="text-3xl font-black">{seasonStats?.punts ?? 0}</div>
          </div>
          <div className="bg-zinc-800 rounded-2xl p-6 text-center">
            <div className="text-sm text-zinc-500 mb-1">Punt Yards</div>
            <div className="text-3xl font-black">{seasonStats?.punt_yards ?? 0}</div>
          </div>
        </div>
      </div>
    )}
  </div>
)}

          </div>


          
        </div>
      </div>

      {/* FOOTER */}
      <footer className="bg-black border-t border-zinc-800 py-16">
        <div className="max-w-7xl mx-auto px-6 text-center text-zinc-500">
          <p>© 2026 Helmets Off Football • Serratox</p>
        </div>
      </footer>
    </div>
  );
}

export default PlayerPage;